/* eslint-disable no-new,max-len,no-unused-expressions,no-nested-ternary */
/* global G, PLATFORM */
import { curry, getFirstItem, isObj, isStr, PlatformEvent, setKey } from 'lib/util'
import { parseURL } from 'whatwg-url-without-unicode'
import redirectSequence from 'lib/sequence/module/adapter/router/redirect'
import search from 'app/_shared/events/search'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import routeComposition from 'trait/composition/route'
import getUserRole from 'app/_shared/events/user/role'
import session from 'app/_shared/session'

/**
 * Shows a dialog informing the user that the scanned code is a URL and asks them whether they want
 * to open it.
 *
 * @param {Gaia.AppModule.Spec} module  current module composition object
 * @param {string} url                  scanned url
 * @returns {Promise<boolean>}           whether the user wanted to open the URL
 */
const showUrlDialog = async (module, url) => await showBlockingDialog(module, null, {
  ...(url.includes('tel') && {
    title: {
      ns: 'common',
      key: 'dialog.scan.url.title.phone',
      defaultValue: 'Phone number',
    },
  }) || (url.includes('mailto') && {
    title: {
      ns: 'common',
      key: 'dialog.scan.url.title.mail',
      defaultValue: 'Email Address',
    },
  }) || {
    title: {
      ns: 'common',
      key: 'dialog.scan.url.title.website',
      defaultValue: 'Website Address',
    },
  },
  text: {
    ns: 'common',
    key: 'dialog.scan.url.textMD',
    defaultValue: 'Scanned **{{url}}**.',
    md: true,
    url: url.replace(/^(tel|mailto):/g, ''),
  },
  ok: {
    ns: 'common',
    key: 'button.open',
    defaultValue: 'Open',
  },
})

/**
 * @typedef {Function} UnknownDialogFn
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {string} code                 a non-recognizable code
 * @returns Promise<boolean>
 */

/**
 * Shows a dialog informing the user that `code` cannot be recognized.
 *
 * @param {string} [action='equipment']
 *
 * @returns {UnknownDialogFn}
 */
const showUnknownCodeDialog = (action = 'equipment') => async (module, code) => await showBlockingDialog(module, null, {
  title: {
    ns: 'common',
    key: `dialog.scan.unknown${action === 'part' ? 'Part' : 'Code'}.title`,
    defaultValue: 'Unknown code',
  },
  text: {
    ns: 'common',
    key: `dialog.scan.unknown${action === 'part' ? 'Part' : 'Code'}.textMD`,
    defaultValue: 'The code **{{code}}** cannot be recognised.',
    md: true,
    code,
  },
  ok: {
    ns: 'common',
    key: 'button.copy',
    defaultValue: 'Copy',
  },
})

/**
 * Shows a dialog informing the user that `code` cannot be recognized.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {PinError} error                 a non-recognizable code
 * @returns {Promise<boolean>}
 */
const showOutdatedKeyDialog = async (module, error) => await showBlockingDialog(module, null, {
  title: {
    ns: 'common',
    key: 'dialog.error.confirmation.default.title',
    defaultValue: 'warning',
  },
  text: {
    ns: 'optionManagement',
    key: error.messageKey,
    defaultValue: error.message,
  },
  ok: {
    ns: 'common',
    key: 'button.ok',
    defaultValue: 'Ok',
  },
  cancel: null,
})

/**
 * Shows a dialog informing the user that the decryption of the code failed.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {PinError} error              a non-recognizable code
 * @returns {Promise<*>}
 */
const showDecryptionFailedDialog = async (module, error) => await showBlockingDialog(module, null, {
  title: {
    ns: 'common',
    key: 'dialog.error.generic.title',
    defaultValue: 'Error',
  },
  text: {
    ns: 'optionManagement',
    key: error.messageKey,
    defaultValue: error.message,
  },
  ok: {
    ns: 'common',
    key: 'button.ok',
    defaultValue: 'Ok',
  },
  cancel: null,
})

/**
 * Redirects to the `generate` action in the `optionManagement` module.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {'option'|'pin'} type         type of the code
 * @param {Object} payload              OptionManagement QR code payload
 * @returns {Promise<*>}
 */
const redirectToOptionManagement = async (module, type, payload) => {
  const moduleState = module[G.STATE]
  moduleState[G.ROUTE] = routeComposition('optionManagement', 'generate')
  return await redirectSequence(module)({ type, payload })
}

/**
 * Sets `key` as the model's state ref and redirects to `action` in equipmentInformation module.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @param {string} action
 * @param {string} key                  key of the equipment to redirect to
 * @returns {Promise<*>}
 */
const redirectToEquipmentDetail = async (module, action, key) => {
  const moduleState = module[G.STATE]
  moduleState[G.ROUTE] = routeComposition('equipmentInformation', action)
  setKey(key, G.REF, module[G.MODEL][G.STATE])
  return await redirectSequence(module)({ key })
}

/**
 * Asynchronously retrieves equipment information based on a serial number.
 *
 * @param {Gaia.AppModule.Spec} module      the current module composition object
 * @param {Gaia.Component.Spec} component   the current action's main component
 * @param {PlatformEvent} event             information about the event that triggered this handler
 * @returns {Promise<Object|null>}
 */
const getEquipmentInfoFromSerial = async (module, component, event) => {
  try {
    const { serial } = event.detail || {}
    const searchEvent = new PlatformEvent('search', { filter: { serialLong: serial }, type: 'equipment' })
    const result = await search('verbose', module, component, searchEvent)

    return getFirstItem(result)
  } catch (e) {
    console.error(e)
  }

  return null
}

/**
 * Asynchronously creates a pin from a provided code and retrieves associated equipment information.
 *
 * @param {Gaia.AppModule.Spec} module      the current module composition object
 * @param {Gaia.Component.Spec} component   the current action's main component
 * @param {string} value                    the code used to create the pin.
 * @returns {Promise<ParsedPin & {equipment: (Object|null)}>}
 */
const createPinFromCode = async (module, component, value) => {
  const pinAdapter = module[G.ADAPTER][G.PIN]

  const { pin, software, serial, request, error } = /** @type ParsedPin */ await pinAdapter.create({ detail: { code: value } })

  const equipment = serial
    ? await getEquipmentInfoFromSerial(module, component, { detail: { serial } })
    : null

  return { equipment: equipment || { key: -1 }, pin, software, serial, request, error }
}

/**
 * Attempts to obtain an equipment or an article whose key contains the string passed in
 * `event.detail.value` and, if found, returns its complete key and the name of its detail
 * action in the equipmentInformation module.
 *
 * @param {Gaia.AppModule.Spec} module      the current module composition object
 * @param {Gaia.Component.Spec} component   the current action's main component
 * @param {PlatformEvent} event             information about the event that triggered this handler
 * @param {Object} event.detail             event details
 * @param {string} event.detail.value       string used to search for the ID of an equipment or
 *                                          article
 * @returns {Promise<[string|null, string]>}
 */
const getEquipmentInfoFromCode = async (module, component, event) => {
  const { value } = event.detail || {}

  const id = value.replace(/[A-Za-z|%]$/g, '')

  // if the string is still a token structure, there was no id to extract
  if (id?.includes?.('{') || id?.includes?.('/')) {
    return [null, 'equipment']
  }

  try {
    const query = `(metatype:equipment AND serialLong:${id}) OR (metatype:article AND number:${id})`
    const searchEvent = new PlatformEvent('search', { query })
    const [item] = await search(null, module, component, searchEvent)

    if (item) {
      const { key, value: { serialLong } } = item
      const isEquipment = !!serialLong // article (part) doesn't have serialLong
      const detailAction = isEquipment ? 'equipment' : 'part'
      return [key, detailAction]
    }
  } catch (e) { console.error(e) }

  // if we don't find anything, fallback to id, if it has dots, it's an article
  return [null, id?.includes?.('.') ? 'part' : 'equipment']
}

/**
 * Shows a 'Decryption failed' dialog.
 *
 * @returns {Promise<*>}
 */
const decryptionFailedResponse = async module => await showDecryptionFailedDialog(module, {
  message: 'An error occurred: The QR-Code is invalid.',
  messageKey: 'optionmanagement.error.invalid-signature',
})

/**
 * Process Scanned Code Event Handler
 *
 * Attempts to recognize a scanned code and display a dialog accordingly:
 * - If code is a URL, the dialog asks the user whether they want to open it.
 * - If code is a QR code, it attempts to parse the code string as JSON:
 *  - If code can be parsed:
 *    - If code is an object with payload and signature properties, it redirects to OpenManagement.
 *    - Otherwise, it's considered a PIN and will be parsed using {@link createPinFromCode}.
 *  - If code cannot be parsed, it shows a dialog informing that the code cannot be recognized.
 * - If code isn't a QR code, attempts to find an article whose key contains the code string:
 *  - If an article is found, it redirects the user to it.
 *  - Otherwise, it shows a dialog informing that the code cannot be recognized.
 *
 * If the result of displaying a dialog is `true` (i.e. the user has clicked the 'ok' option), in
 * most cases, this event handler returns an object to be handled by the component that called it.
 *
 * @param {Gaia.AppModule.Spec} module      the current module composition object
 * @param {Gaia.Component.Spec} component   the current action's main component
 * @param {Gaia.PlatformEvent} event        information about the event that triggered this handler
 * @param {Object} event.detail             event details
 * @param {string} event.detail.type        the type of scanned code
 * @param {string} event.detail.value       the scanned code
 * @returns {Promise<Object|boolean>}
 */
const processScannedCode = async (module, component, event) => {
  const { user } = session(module)
  const { type = 'qr', value } = event.detail || {}
  const [key, action] = await getEquipmentInfoFromCode(module, component, event)

  try {
    const url = PLATFORM === 'mobile'
      ? parseURL(value, {})
      : URL.parse(value)

    console.log('Scanned value:', value)

    if (url) {
      return await showUrlDialog(module, value) && { link: value }
    }

    const userRole = getUserRole(user.ref()?.value?.roles, module, component, null)?.key

    if (type === 'qr') {
      try {
        if (!value.includes('{')) throw Error // it's a pasted code, continue below

        const valueObj = JSON.parse(value.replace(/\n/g, ''))
        const isPasswordCode = isObj(valueObj) && valueObj.payload && valueObj.signature

        return isPasswordCode && userRole === 'PinCreatorOnly_c'
          ? await decryptionFailedResponse(module)
          : isPasswordCode
            ? await redirectToOptionManagement(module, 'option', { request: valueObj }) && { disable: true }
            : await showUnknownCodeDialog(action)(module, value) && { clipboard: value }
      } catch (ignored) {}

      const pinObj = await createPinFromCode(module, component, value)

      const { error } = pinObj

      if (error) {
        error.type === 'outdated'
          ? await showOutdatedKeyDialog(module, error)
          : await showDecryptionFailedDialog(module, error) && throw error
      }

      const { request, ...data } = pinObj

      return isObj(pinObj) && !!pinObj.pin
        ? await redirectToOptionManagement(module, 'pin', {
          request,
          ...error?.messageKey ? { response: { messageKey: error.messageKey, message: error.message } } : {},
          data,
        }) && { disable: true }
        : await showUnknownCodeDialog(action)(module, pinObj.pin) && { clipboard: pinObj.pin }
    }

    if (userRole === 'PinCreatorOnly_c') {
      return decryptionFailedResponse(module)
    }
  } catch (e) {
    console.error(e)
  }

  return key
    ? await redirectToEquipmentDetail(module, action, key) && { disable: true }
    : await showUnknownCodeDialog(action)(module, value) && { clipboard: value }
}

export default curry(processScannedCode)
